// Description: Header that will have functionalities like search data or download
// Updated: 02/09/2025
// Author: Simon Franklin

// React
import React from 'react';

// Material UI Components
import { Box, Button, createTheme, FormControl, TextField, ThemeProvider } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Components and Functions for the project
import dayjs from 'dayjs';
import themeColors from '../ThemeColors/ThemeColors';

const ModeGraphicModalFilter = ({ setStartDate, setEndDate, startDate, endDate, downloadExcel, loadingExcel, searchCampaigns, loadingSearch, loadingLabels, fetchDataToNewTable, loadingTable }) => {

    const startDateTheme = createTheme({
        components: {
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#FFF',
                        color: themeColors.gray30,
                        borderRadius: '8px',
                    },
                },
            },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        color: themeColors.purple400,
                        borderRadius: '2px',
                        borderWidth: '1px',
                        border: '1px solid',
                        backgroundColor: '#FFF',
                        '&:hover': {
                            backgroundColor: themeColors.purple400,
                            color: '#FFF',
                        },
                        '&.Mui-selected': {
                            backgroundColor: themeColors.purple400 + " !important",
                            color: '#FFF !important',
                        }
                    }
                }
            }
        }
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                padding: '20px',
                marginTop: '20px',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <FormControl variant="outlined" sx={{ flex: 1 }}>
                <ThemeProvider theme={startDateTheme}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                        <DesktopDatePicker
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            maxDate={dayjs()}
                            InputLabelProps={{ shrink: true }}
                            renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                            inputFormat="DD/MM/YYYY"
                            sx={{
                                '& .css-12qs7hp-MuiInputBase-input-MuiOutlinedInput-input': {
                                    padding: '10px'
                                },
                            }}
                        />
                    </LocalizationProvider>
                </ThemeProvider>
            </FormControl>

            <FormControl variant="outlined" sx={{ flex: 1 }}>
                <ThemeProvider theme={startDateTheme}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                        <DesktopDatePicker
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            maxDate={dayjs()}
                            InputLabelProps={{ shrink: true }}
                            renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                            inputFormat="DD/MM/YYYY"
                            sx={{
                                '& .css-12qs7hp-MuiInputBase-input-MuiOutlinedInput-input': {
                                    padding: '10px'
                                },
                            }}
                        />
                    </LocalizationProvider>
                </ThemeProvider>
            </FormControl>

            <Button
                variant="contained"
                sx={{
                    flex: 1,
                    padding: { xs: '15px', sm: '0px' },
                    borderRadius: '6px',
                    backgroundColor:
                        loadingSearch || loadingExcel || loadingLabels || loadingTable
                            ? `${themeColors.gray10} !important`
                            : themeColors.purple300,
                    color:
                        loadingSearch || loadingExcel || loadingLabels || loadingTable
                            ? `${themeColors.gray40} !important`
                            : themeColors.purple950,
                    '&:hover': {
                        backgroundColor: themeColors.purple400 + 'CC',
                    },
                }}
                onClick={searchCampaigns}
                disabled={loadingSearch || loadingExcel || loadingLabels || loadingTable}
            >
                {loadingSearch
                    ? 'Pesquisando...'
                    : loadingExcel || loadingLabels || loadingTable
                        ? 'Aguarde...'
                        : 'Pesquisar'}
            </Button>

            <Button
                variant="contained"
                sx={{
                    flex: 1,
                    padding: { xs: '15px', sm: '0px' },
                    borderRadius: '6px',
                    backgroundColor:
                        loadingSearch || loadingExcel || loadingLabels || loadingTable
                            ? `${themeColors.gray10} !important`
                            : themeColors.green01,
                    color:
                        loadingSearch || loadingExcel || loadingLabels || loadingTable
                            ? `${themeColors.gray40} !important`
                            : themeColors.green03,
                    '&:hover': {
                        backgroundColor: themeColors.green01 + 'CC',
                    },
                }}
                onClick={downloadExcel}
                disabled={loadingSearch || loadingExcel || loadingLabels || loadingTable}
            >
                {loadingExcel
                    ? 'Baixando...'
                    : loadingSearch || loadingLabels || loadingTable
                        ? 'Aguarde...'
                        : 'Baixar Excel'}
            </Button>

            <Button
                variant="contained"
                sx={{
                    flex: 1,
                    padding: { xs: '15px', sm: '0px' },
                    borderRadius: '6px',
                    backgroundColor:
                        loadingSearch || loadingExcel || loadingLabels || loadingTable
                            ? `${themeColors.gray10} !important`
                            : themeColors.purple500,
                    color:
                        loadingSearch || loadingExcel || loadingLabels || loadingTable
                            ? `${themeColors.gray40} !important`
                            : '#fff',
                    '&:hover': {
                        backgroundColor: themeColors.purple600,
                    },
                    animation: !loadingSearch && !loadingExcel && !loadingLabels && !loadingTable
                        ? 'subtlePulse 2.5s infinite'
                        : 'none',
                    '@keyframes subtlePulse': {
                        '0%': {
                            boxShadow: `0 0 0 0 ${themeColors.purple500}22`,
                            transform: 'scale(1)',
                        },
                        '50%': {
                            boxShadow: `0 0 6px 4px ${themeColors.purple500}11`,
                            transform: 'scale(1.01)',
                        },
                        '100%': {
                            boxShadow: `0 0 0 0 rgba(0, 0, 0, 0)`,
                            transform: 'scale(1)',
                        },
                    },
                }}
                onClick={fetchDataToNewTable}
                disabled={loadingSearch || loadingExcel || loadingLabels || loadingTable}
            >
                {loadingExcel || loadingSearch || loadingLabels
                    ? 'Aguarde...'
                    : loadingTable
                        ? 'Buscando...'
                        : 'Gerar tabela dinâmica'}
            </Button>

        </Box>

    );
};

export default ModeGraphicModalFilter;